/**
 * iter-232 Phase 10 — Transfer Hold / Release controls.
 *
 * Inline pill that drops next to any Razorpay transfer id. Lets admin:
 *   - Place the transfer on hold (with optional `on_hold_until` deadline)
 *   - Release a hold immediately (settlement will go out on the next
 *     working-day cycle after the LA's defined settlement schedule
 *     — per Razorpay Doc #14 + #26).
 *
 * Calls PATCH /api/admin/razorpay/transfers/{transfer_id}/hold
 * (services.razorpay_transfers.modify_settlement_hold).
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import { Loader2, Pause, Play, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const TransferHoldControls = ({ transferId, currentlyOnHold = false, onChanged }) => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [intent, setIntent] = useState(null); // 'hold' | 'release'
  const [holdUntil, setHoldUntil] = useState(''); // YYYY-MM-DDTHH:MM (datetime-local input)
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const startIntent = (next) => {
    setIntent(next);
    setResult(null);
    setHoldUntil('');
    setOpen(true);
  };

  const submit = async () => {
    if (!transferId) return;
    setRunning(true);
    setResult(null);
    try {
      const body = { on_hold: intent === 'hold' };
      if (intent === 'hold' && holdUntil) {
        // datetime-local gives "2026-02-14T15:30"; convert to unix seconds.
        const ts = Math.floor(new Date(holdUntil).getTime() / 1000);
        if (ts > 0) body.on_hold_until = ts;
      }
      const res = await apiCall(
        `/api/admin/razorpay/transfers/${transferId}/hold`,
        {
          method: 'PATCH',
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json' },
        },
      );
      setResult({ ok: true, body: res });
      toast({
        title: intent === 'hold' ? 'Hold applied' : 'Hold released',
        description: `transfer ${transferId}`,
      });
      if (typeof onChanged === 'function') onChanged(res);
    } catch (err) {
      const msg = err?.message || 'Operation failed';
      setResult({ ok: false, error: msg });
      toast({ title: 'Failed', description: msg, variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <div className="inline-flex gap-1" data-testid={`transfer-hold-controls-${transferId}`}>
        {currentlyOnHold ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => startIntent('release')}
            data-testid={`transfer-release-btn-${transferId}`}
            title="Release this transfer's hold — payout will be queued for next settlement cycle."
            className="h-6 px-2 text-xs"
          >
            <Play className="h-3 w-3 mr-1" /> Release
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => startIntent('hold')}
            data-testid={`transfer-hold-btn-${transferId}`}
            title="Place this transfer on hold — payout pauses until released."
            className="h-6 px-2 text-xs"
          >
            <Pause className="h-3 w-3 mr-1" /> Hold
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => !running && setOpen(v)}>
        <DialogContent className="sm:max-w-md" data-testid="transfer-hold-dialog">
          <DialogHeader>
            <DialogTitle>
              {intent === 'hold' ? 'Place transfer on hold' : 'Release transfer hold'}
            </DialogTitle>
          </DialogHeader>

          <Alert className="my-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Settlement schedule wins</AlertTitle>
            <AlertDescription className="text-xs">
              The Linked Account&apos;s defined settlement schedule (e.g.
              T+10) takes precedence over <code>on_hold_until</code>.
              Releasing on T+15 with a T+10 schedule means payout still
              goes out the next working day after release — not on T+10.
            </AlertDescription>
          </Alert>

          <div className="space-y-3 py-2 text-sm">
            <div className="text-xs text-gray-500">
              Transfer: <code>{transferId}</code>
            </div>
            {intent === 'hold' && (
              <div className="space-y-1">
                <Label htmlFor="hold-until">Release at (optional)</Label>
                <Input
                  id="hold-until"
                  data-testid="transfer-hold-until-input"
                  type="datetime-local"
                  value={holdUntil}
                  onChange={(e) => setHoldUntil(e.target.value)}
                  disabled={running}
                />
                <p className="text-xs text-gray-500">
                  Leave empty for indefinite hold. Razorpay enforces
                  <code> on_hold_until</code> in the future (≥ 30 min recommended).
                </p>
              </div>
            )}
          </div>

          {result && (
            <Alert
              variant={result.ok ? 'default' : 'destructive'}
              className="mt-2"
              data-testid="transfer-hold-result"
            >
              {result.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              <AlertTitle>{result.ok ? 'Updated' : 'Failed'}</AlertTitle>
              <AlertDescription className="text-xs font-mono">
                {result.ok
                  ? `on_hold: ${result.body?.data?.on_hold ?? '?'}, settlement_status: ${result.body?.data?.settlement_status ?? '?'}`
                  : result.error}
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={running} data-testid="transfer-hold-cancel">
              {result?.ok ? 'Close' : 'Cancel'}
            </Button>
            {!result?.ok && (
              <Button onClick={submit} disabled={running} data-testid="transfer-hold-submit">
                {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {intent === 'hold' ? 'Place on hold' : 'Release hold'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TransferHoldControls;
